// Запросы Garmin: /api/garmin/*. Статус используют настройки, плашка и экран активностей.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'

export type GarminAccountStatus = 'disconnected' | 'connected' | 'expired' | 'error' | 'rate_limited'

export type GarminStatus = {
  status: GarminAccountStatus
  message: string
  email_hint: string | null
  connected: boolean
  syncing: boolean
  last_sync_at: string | null
  last_success_at: string | null
  last_error: string | null
  retry_at: string | null
  mfa_pending: boolean
  backfill: { loaded: number; total: number; done: boolean }
  banner: { kind: 'expired' | 'stale'; message: string } | null
  steps_goal: number | null
}

export type LoginResponse = { status: 'connected' | 'needs_mfa'; garmin: GarminStatus }

export type SyncRun = {
  id: string
  kind: string
  started_at: string
  finished_at: string | null
  status: string
  summary: {
    days?: Record<string, Record<string, string>>
    written?: number
    activities?: number
    rejected?: string[]
  } | null
  error: string | null
}

export const garminKeys = {
  all: ['garmin'] as const,
  status: ['garmin', 'status'] as const,
  runs: ['garmin', 'runs'] as const,
}

export const garminApi = {
  status: () => api.get<GarminStatus>('/garmin/status'),
  login: (email: string, password: string) => api.post<LoginResponse>('/garmin/login', { email, password }),
  mfa: (code: string) => api.post<LoginResponse>('/garmin/mfa', { code }),
  disconnect: () => api.post<GarminStatus>('/garmin/disconnect', {}),
  sync: () => api.post<SyncRun>('/garmin/sync', {}),
  runs: () => api.get<SyncRun[]>('/garmin/sync-runs'),
}

export function useGarminStatus(options: { refetchInterval?: number | false } = {}) {
  return useQuery({
    queryKey: garminKeys.status,
    queryFn: garminApi.status,
    refetchInterval: options.refetchInterval ?? 5 * 60_000,
    staleTime: 30_000,
  })
}

export function useSyncRuns(enabled = true) {
  return useQuery({ queryKey: garminKeys.runs, queryFn: garminApi.runs, enabled })
}

function useInvalidateGarmin() {
  const client = useQueryClient()
  return () => client.invalidateQueries({ queryKey: garminKeys.all })
}

export function useGarminLogin() {
  const invalidate = useInvalidateGarmin()
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) => garminApi.login(email, password),
    onSuccess: invalidate,
  })
}

export function useGarminMfa() {
  const invalidate = useInvalidateGarmin()
  return useMutation({ mutationFn: (code: string) => garminApi.mfa(code), onSuccess: invalidate })
}

export function useGarminDisconnect() {
  const invalidate = useInvalidateGarmin()
  return useMutation({ mutationFn: garminApi.disconnect, onSuccess: invalidate })
}

export function useGarminSync() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: garminApi.sync,
    onSettled: () => {
      void client.invalidateQueries({ queryKey: garminKeys.all })
      void client.invalidateQueries({ queryKey: ['metrics'] })
      void client.invalidateQueries({ queryKey: ['activities'] })
      void client.invalidateQueries({ queryKey: ['sleep'] })
      void client.invalidateQueries({ queryKey: ['today'] })
    },
  })
}
